import {
  EULER_EARN_VAULT_LENS_ADDRESS,
  VAULT_LENS_ADDRESS,
  vaultLensAbi,
  eulerEarnVaultLensAbi,
} from '@/config/euler-contracts'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { type Address, type PublicClient, formatUnits } from 'viem'
import {
  calculateSupplyAPY,
  calculateTotalAssetsUSD,
  calculateUtilization,
  fetchAssetInfo,
  fetchBatchAssetInfo,
} from '../shared/onchainHelpers'
import type { NormalizedELendInfo } from './types'

/**
 * Fetch ELend vault info directly from onchain (fallback when indexer is unavailable)
 */
export async function fetchELendOnchain(
  publicClient: PublicClient,
  chainId: number,
  address: Address,
): Promise<NormalizedELendInfo> {
  const contracts = [
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'name',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'symbol',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'decimals',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'asset',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalAssets',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalSupply',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalBorrows',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'cash',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestRate',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestFee',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'governorAdmin',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'oracle',
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'unitOfAccount',
    },
  ] as const

  const results = await publicClient.multicall({ contracts })

  const name = results[0].status === 'success' ? (results[0].result as string) : 'Unknown Vault'
  const symbol = results[1].status === 'success' ? (results[1].result as string) : 'UNKNOWN'
  const decimals = results[2].status === 'success' ? (results[2].result as number) : 18
  const asset = results[3].status === 'success' ? (results[3].result as Address) : null
  const totalAssets = results[4].status === 'success' ? (results[4].result as bigint) : 0n
  const totalShares = results[5].status === 'success' ? (results[5].result as bigint) : 0n
  const totalBorrows = results[6].status === 'success' ? (results[6].result as bigint) : 0n
  const cash = results[7].status === 'success' ? (results[7].result as bigint) : 0n
  const interestRate = results[8].status === 'success' ? (results[8].result as bigint) : 0n
  const interestFee = results[9].status === 'success' ? BigInt(results[9].result as number | bigint) : 0n
  const governorAdmin = results[10].status === 'success' ? (results[10].result as Address) : null
  const oracle = results[11].status === 'success' ? (results[11].result as Address) : null
  const unitOfAccount = results[12].status === 'success' ? (results[12].result as Address) : null

  // Fetch asset info and price if we have an asset address
  let assetSymbol: string | null = null
  let assetDecimals: number | null = null
  let assetPrice: number | null = null
  let totalAssetsUSD = 0

  if (asset) {
    const assetInfo = await fetchAssetInfo(publicClient, asset, address)
    if (assetInfo) {
      assetSymbol = assetInfo.symbol
      assetDecimals = assetInfo.decimals
      assetPrice = assetInfo.priceUSD
      totalAssetsUSD = calculateTotalAssetsUSD(totalAssets, assetInfo.decimals, assetInfo.priceUSD)
    }
  }

  // Calculate APY from interest rate
  let baseApy: number | null = null
  let utilization: number | null = null

  if (interestRate > 0n && totalAssets > 0n) {
    utilization = calculateUtilization(totalBorrows, totalAssets)
    baseApy = calculateSupplyAPY(interestRate, utilization, interestFee)
  }

  return {
    chainId,
    address,
    name,
    symbol,
    decimals,
    asset,
    assetSymbol,
    assetDecimals,
    assetPrice,
    oracle,
    unitOfAccount,
    totalBorrows,
    cash,
    totalAssets,
    totalShares,
    totalAssetsUSD,
    baseApy,
    totalApy: baseApy, // Without rewards data
    intrinsicApy: null, // Not available from onchain
    supplyApy7d: null,
    supplyApy30d: null,
    supplyApy90d: null,
    borrowApy7d: null,
    borrowApy30d: null,
    borrowApy90d: null,
    utilization,
    supplyCap: null,
    governorAdmin,
    governorType: null,
    perspectives: [],
    entities: [],
    products: [],
    rewardsMetadata: [],
    exposure: null,
    points: [],
    source: 'onchain',
  }
}

/**
 * Enrich ELend vaults with oracle prices for assets missing price data.
 * Uses batched multicalls instead of individual RPC calls to avoid rate-limiting.
 *
 * Pass 1: VaultLens.getVaultInfoFull for all vaults (works for lending vaults)
 * Pass 2: EulerEarnVaultLens for vaults that failed pass 1 (Earn vaults)
 * Pass 3: VaultLens on discovered strategy addresses from pass 2
 */
export async function enrichELendsWithOraclePrices(
  publicClient: PublicClient,
  vaults: NormalizedELendInfo[],
): Promise<NormalizedELendInfo[]> {
  const vaultsNeedingPrices = vaults.filter(
    (vault) => vault.asset && (vault.assetPrice === null || vault.assetPrice === 0),
  )

  if (vaultsNeedingPrices.length === 0) return vaults

  const priceMap = new Map<Address, number>()

  // Pass 1: batch VaultLens call for all vaults (lending vaults succeed, Earn vaults fail gracefully)
  const lensContracts = vaultsNeedingPrices.map((vault) => ({
    address: VAULT_LENS_ADDRESS as Address,
    abi: vaultLensAbi,
    functionName: 'getVaultInfoFull' as const,
    args: [vault.address] as const,
  }))

  const lensResults = await publicClient.multicall({ contracts: lensContracts })

  const unresolvedVaults: NormalizedELendInfo[] = []

  for (let i = 0; i < vaultsNeedingPrices.length; i++) {
    const vault = vaultsNeedingPrices[i]!
    const result = lensResults[i]
    if (result?.status === 'success' && result.result) {
      const info = result.result as any
      const priceInfo = info.liabilityPriceInfo
      if (priceInfo && !priceInfo.queryFailure) {
        const decimals = typeof info.unitOfAccountDecimals === 'bigint'
          ? Number(info.unitOfAccountDecimals)
          : (typeof info.unitOfAccountDecimals === 'number' ? info.unitOfAccountDecimals : 18)
        const price = Number(formatUnits(priceInfo.amountOutMid as bigint, decimals))
        if (price > 0) {
          priceMap.set(vault.address, price)
          continue
        }
      }
    }
    unresolvedVaults.push(vault)
  }

  // Pass 2: batch EulerEarnVaultLens for unresolved vaults (Earn vaults → find strategy)
  if (unresolvedVaults.length > 0) {
    const earnContracts = unresolvedVaults.map((vault) => ({
      address: EULER_EARN_VAULT_LENS_ADDRESS as Address,
      abi: eulerEarnVaultLensAbi,
      functionName: 'getVaultInfoFull' as const,
      args: [vault.address] as const,
    }))

    const earnResults = await publicClient.multicall({ contracts: earnContracts })

    // Collect strategy addresses for pass 3
    const strategyEntries: { vaultAddress: Address; strategyAddress: Address }[] = []

    for (let i = 0; i < unresolvedVaults.length; i++) {
      const result = earnResults[i]
      if (result?.status !== 'success' || !result.result) continue
      const strategies = (result.result as any).strategies as any[] | undefined
      const evaultStrategy = strategies?.find((s: any) => s.info?.isEVault)
      if (evaultStrategy?.strategy) {
        strategyEntries.push({
          vaultAddress: unresolvedVaults[i]!.address,
          strategyAddress: evaultStrategy.strategy as Address,
        })
      }
    }

    // Pass 3: batch VaultLens on discovered strategies
    if (strategyEntries.length > 0) {
      const strategyContracts = strategyEntries.map((entry) => ({
        address: VAULT_LENS_ADDRESS as Address,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull' as const,
        args: [entry.strategyAddress] as const,
      }))

      const strategyResults = await publicClient.multicall({ contracts: strategyContracts })

      for (let i = 0; i < strategyEntries.length; i++) {
        const result = strategyResults[i]
        if (result?.status !== 'success' || !result.result) continue
        const info = result.result as any
        const priceInfo = info.liabilityPriceInfo
        if (priceInfo && !priceInfo.queryFailure) {
          const decimals = typeof info.unitOfAccountDecimals === 'bigint'
            ? Number(info.unitOfAccountDecimals)
            : (typeof info.unitOfAccountDecimals === 'number' ? info.unitOfAccountDecimals : 18)
          const price = Number(formatUnits(priceInfo.amountOutMid as bigint, decimals))
          if (price > 0) {
            priceMap.set(strategyEntries[i]!.vaultAddress, price)
          }
        }
      }
    }
  }

  const enrichedVaults = vaults.map((vault) => {
    if (vault.assetPrice !== null && vault.assetPrice > 0) return vault

    const price = priceMap.get(vault.address)
    if (!price) return vault

    return {
      ...vault,
      assetPrice: price,
      totalAssetsUSD: calculateTotalAssetsUSD(vault.totalAssets, vault.assetDecimals ?? 18, price),
    }
  })

  return enrichedVaults.sort((a, b) => b.totalAssetsUSD - a.totalAssetsUSD)
}

/**
 * Batch fetch multiple ELend vaults from onchain
 */
export async function fetchELendsOnchain(
  publicClient: PublicClient,
  chainId: number,
  addresses: Address[],
): Promise<Map<Address, NormalizedELendInfo>> {
  const vaultMap = new Map<Address, NormalizedELendInfo>()

  if (addresses.length === 0) {
    return vaultMap
  }

  // Build contracts array for all vaults
  const contracts = addresses.flatMap((address) => [
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'name' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'symbol' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'decimals' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'asset' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalAssets' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalSupply' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'totalBorrows' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'cash' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestRate' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'interestFee' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'governorAdmin' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'oracle' as const,
    },
    {
      address,
      abi: eVaultImplementationAbi,
      functionName: 'unitOfAccount' as const,
    },
  ])

  const results = await publicClient.multicall({ contracts })

  // Collect asset addresses and their corresponding vault addresses for batch fetching
  const assetAddresses: (Address | null)[] = []
  for (let i = 0; i < addresses.length; i++) {
    const baseIndex = i * 13
    const assetResult = results[baseIndex + 3]
    const asset = assetResult?.status === 'success' ? (assetResult.result as Address) : null
    assetAddresses.push(asset)
  }

  // Build parallel arrays of valid assets and their vault addresses for VaultLens pricing
  const validAssets: Address[] = []
  const validVaultAddresses: Address[] = []
  for (let i = 0; i < assetAddresses.length; i++) {
    const asset = assetAddresses[i]
    const vaultAddr = addresses[i]
    if (asset && vaultAddr) {
      validAssets.push(asset)
      validVaultAddresses.push(vaultAddr)
    }
  }

  const assetInfoMap = await fetchBatchAssetInfo(publicClient, validAssets, validVaultAddresses)

  // Process results in chunks of 13 per vault
  for (let i = 0; i < addresses.length; i++) {
    const baseIndex = i * 13
    const address = addresses[i]

    if (!address) continue

    const nameResult = results[baseIndex]
    const symbolResult = results[baseIndex + 1]
    const decimalsResult = results[baseIndex + 2]
    const assetResult = results[baseIndex + 3]
    const totalAssetsResult = results[baseIndex + 4]
    const totalSupplyResult = results[baseIndex + 5]
    const totalBorrowsResult = results[baseIndex + 6]
    const cashResult = results[baseIndex + 7]
    const interestRateResult = results[baseIndex + 8]
    const interestFeeResult = results[baseIndex + 9]
    const governorAdminResult = results[baseIndex + 10]
    const oracleResult = results[baseIndex + 11]
    const unitOfAccountResult = results[baseIndex + 12]

    const name = nameResult?.status === 'success' ? (nameResult.result as string) : 'Unknown Vault'
    const symbol = symbolResult?.status === 'success' ? (symbolResult.result as string) : 'UNKNOWN'
    const decimals = decimalsResult?.status === 'success' ? (decimalsResult.result as number) : 18
    const asset = assetResult?.status === 'success' ? (assetResult.result as Address) : null
    const totalAssets = totalAssetsResult?.status === 'success' ? (totalAssetsResult.result as bigint) : 0n
    const totalShares = totalSupplyResult?.status === 'success' ? (totalSupplyResult.result as bigint) : 0n
    const totalBorrows = totalBorrowsResult?.status === 'success' ? (totalBorrowsResult.result as bigint) : 0n
    const cash = cashResult?.status === 'success' ? (cashResult.result as bigint) : 0n
    const interestRate = interestRateResult?.status === 'success' ? (interestRateResult.result as bigint) : 0n
    const interestFee =
      interestFeeResult?.status === 'success' ? BigInt(interestFeeResult.result as number | bigint) : 0n
    const governorAdmin = governorAdminResult?.status === 'success' ? (governorAdminResult.result as Address) : null
    const oracle = oracleResult?.status === 'success' ? (oracleResult.result as Address) : null
    const unitOfAccount = unitOfAccountResult?.status === 'success' ? (unitOfAccountResult.result as Address) : null

    const assetInfo = asset ? assetInfoMap.get(asset) : undefined

    // Calculate TVL if we have asset price
    let totalAssetsUSD = 0
    let assetPrice: number | null = null
    if (assetInfo) {
      assetPrice = assetInfo.priceUSD
      totalAssetsUSD = calculateTotalAssetsUSD(totalAssets, assetInfo.decimals, assetInfo.priceUSD)
    }

    // Calculate APY
    let baseApy: number | null = null
    let utilization: number | null = null

    if (interestRate > 0n && totalAssets > 0n) {
      utilization = calculateUtilization(totalBorrows, totalAssets)
      baseApy = calculateSupplyAPY(interestRate, utilization, interestFee)
    }

    vaultMap.set(address, {
      chainId,
      address,
      name,
      symbol,
      decimals,
      asset,
      assetSymbol: assetInfo?.symbol ?? null,
      assetDecimals: assetInfo?.decimals ?? null,
      assetPrice,
      oracle,
      unitOfAccount,
      totalBorrows,
      cash,
      totalAssets,
      totalShares,
      totalAssetsUSD,
      baseApy,
      totalApy: baseApy,
      intrinsicApy: null, // Not available from onchain
      supplyApy7d: null,
      supplyApy30d: null,
      supplyApy90d: null,
      borrowApy7d: null,
      borrowApy30d: null,
      borrowApy90d: null,
      utilization,
      supplyCap: null,
      governorAdmin,
      governorType: null,
      perspectives: [],
      entities: [],
      products: [],
      rewardsMetadata: [],
      exposure: null,
      points: [],
      source: 'onchain',
    })
  }

  return vaultMap
}
